import { Alert, Button, CircularProgress, FormControlLabel, Switch } from '@mui/material';
import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useBooth } from '../../../../../../../hooks/useBooth';
import { Booth } from '../../../../../../../interfaces/interfaces';
import { setAdminUserBooth } from '../../../../../../../reduxToolkit/slices/adminSlice';
import { updateBooth } from '../../../../../../../services/services';
import VyTextField from '../../../../../../common/VyTextField';
import { TabContentWrapper } from './TabContentWrapper';

const VyRichTextEditor = dynamic(() => import('../../../../../../common/VyRichTextEditor'), {
  ssr: false,
  loading: () => <p>loading...</p>,
});

const BoothAutoReplyTab = ({ booth }: { booth: Booth }) => {
  const [content, setContent] = useState('');
  const [response, setResponse] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { i18n, t } = useTranslation();

  const { register, handleSubmit, control } = useForm();
  const dispatch = useDispatch();
  const { fetchAndUpdateBooth } = useBooth();

  async function submit(props: any) {
    props.settings.text = content;
    props.settings.delayMinutes = parseInt(props.settings.delayMinutes);
    setIsLoading(true);
    await updateBooth({
      autoReply: props,
      boothid: booth._id,
    })
      .then((res: Booth) => {
        dispatch(setAdminUserBooth(res));
        fetchAndUpdateBooth(booth._id);
        setResponse(t('common:success'));
      })
      .catch((err) => {
        setResponse(err?.response?.data?.translation[i18n.language === 'en' ? 'en-EN' : 'de-DE'] || t('common:error-without-status'));
      });
    setIsLoading(false);
  }

  useEffect(() => {
    if (booth) setContent(booth?.autoReply?.settings?.text);
  }, [booth]);

  return (
    <TabContentWrapper>
      <div className="flex flex-col w-full h-full">
        {booth && (
          <>
            <Alert severity="info">{t('account:autoreply-subtitle')}</Alert>
            <form className="flex flex-col space-y-6" onSubmit={handleSubmit(submit)}>
              <label className="flex flex-col mt-4">
                <span className="mb-2">{t('autoreply:message')}</span>
                <VyRichTextEditor defaultValue={booth?.autoReply?.settings?.text} onBlur={(desc: string) => setContent(desc)} />
              </label>
              <label>
                <span>{t('autoreply:delay')}</span>
                <Controller
                  control={control}
                  name={`settings.delayMinutes`}
                  defaultValue={booth.autoReply.settings.delayMinutes}
                  render={({ field }) => <VyTextField {...field} type="number" color="secondary" fullWidth />}
                />
              </label>
              <label className="space-x-4">
                <span>{t('autoreply:enable')}</span>
                <FormControlLabel control={<Switch defaultChecked={booth.autoReply.active} {...register('active')} />} className="checkLabel" label="" />
              </label>
              {!booth.autoReply.active && (
                <Alert className="my-3" severity="warning">
                  {t('autoreply:no-autoreply-warning')}
                </Alert>
              )}
              <label className="flex flex-col">
                <Button style={{ background: 'var(--vystem-blue)', color: 'white' }} type="submit">
                  {t('common:save')}
                </Button>
                {isLoading && (
                  <div className="flex justify-center items-center">
                    <CircularProgress />
                  </div>
                )}
                {response && <div className="flex justify-center items-center">{response}</div>}
              </label>
            </form>
          </>
        )}
      </div>
    </TabContentWrapper>
  );
};
export default BoothAutoReplyTab;
